import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";

const PRIMARY = "#06BBC7";
const BLACK = "#111827";
const YELLOW = "#F7C948";
const YELLOW_HOVER = "#efbd35";

const NavbarMobile = ({
  treatmentItems,
  stylingItems,
  isActive,
  isTreatmentsActive,
  isStylingActive,
}) => {
  const [open, setOpen] = useState(false);
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);
  const [stylingOpen, setStylingOpen] = useState(false);

  const navigate = useNavigate();

  const goTo = (href) => {
    setOpen(false);
    setTreatmentsOpen(false);
    setStylingOpen(false);
    navigate(href);
  };

  return (
    <Box
      sx={{
        display: {
          xs: "flex",
          md: "none",
        },
        alignItems: "center",
        gap: 1,
      }}
    >
      <Button
        component={RouterLink}
        to="/agendar-cita"
        variant="contained"
        sx={{
          bgcolor: YELLOW,
          color: "#1f2937",
          px: 2.2,
          py: 0.9,
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 700,
          fontSize: "0.82rem",
          boxShadow: "none",
          whiteSpace: "nowrap",
          "&:hover": {
            bgcolor: YELLOW_HOVER,
            boxShadow: "none",
          },
        }}
      >
        Agendar Cita
      </Button>

      <IconButton
        component={RouterLink}
        to="/login"
        aria-label="Iniciar sesión"
        sx={{
          color: BLACK,
          borderRadius: "50%",
          p: 0.8,
          backgroundColor: "rgba(6, 187, 199, 0.08)",
          border: "1px solid rgba(17, 24, 39, 0.1)",
          "&:hover": {
            color: PRIMARY,
            backgroundColor: "rgba(6, 187, 199, 0.13)",
          },
        }}
      >
        <LoginRoundedIcon />
      </IconButton>

      <IconButton
        onClick={() => setOpen(true)}
        aria-label="Abrir menú"
        sx={{
          color: BLACK,
          borderRadius: 0,
          p: 0.8,
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: "86%",
            maxWidth: 360,
            backgroundColor: "#ffffff",
            borderRadius: 0,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2.2,
              py: 2,
              borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                color: BLACK,
                fontSize: "0.96rem",
                fontWeight: 700,
              }}
            >
              Menú
            </Typography>

            <IconButton
              onClick={() => setOpen(false)}
              aria-label="Cerrar menú"
              sx={{
                color: BLACK,
                borderRadius: 0,
                p: 0.8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <List
            sx={{
              px: 1.4,
              py: 1.4,
              flex: 1,
            }}
          >
            <ListItemButton
              onClick={() => goTo("/")}
              sx={{
                minHeight: 48,
                mb: 0.6,
                px: 1.4,
                borderRadius: 2,
                color: isActive("/") ? PRIMARY : BLACK,
                backgroundColor: isActive("/")
                  ? "rgba(6, 187, 199, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(6, 187, 199, 0.08)",
                  color: PRIMARY,
                },
              }}
            >
              <ListItemText
                primary="Inicio"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.96rem",
                }}
              />
            </ListItemButton>

            <ListItemButton
              onClick={() => goTo("/acerca-de-nosotros")}
              sx={{
                minHeight: 48,
                mb: 0.6,
                px: 1.4,
                borderRadius: 2,
                color: isActive("/acerca-de-nosotros") ? PRIMARY : BLACK,
                backgroundColor: isActive("/acerca-de-nosotros")
                  ? "rgba(6, 187, 199, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(6, 187, 199, 0.08)",
                  color: PRIMARY,
                },
              }}
            >
              <ListItemText
                primary="Acerca de nosotros"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.96rem",
                }}
              />
            </ListItemButton>

            <ListItemButton
              onClick={() => setTreatmentsOpen((prev) => !prev)}
              sx={{
                minHeight: 48,
                mb: 0.6,
                px: 1.4,
                borderRadius: 2,
                color: isTreatmentsActive() ? PRIMARY : BLACK,
                backgroundColor: isTreatmentsActive()
                  ? "rgba(6, 187, 199, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(6, 187, 199, 0.08)",
                  color: PRIMARY,
                },
              }}
            >
              <ListItemText
                primary="Tratamientos"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.96rem",
                }}
              />

              <KeyboardArrowDownIcon
                sx={{
                  transform: treatmentsOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  color: "inherit",
                }}
              />
            </ListItemButton>

            <Collapse in={treatmentsOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  ml: 1,
                  mb: 0.8,
                  pl: 1.2,
                  borderLeft: "2px solid rgba(6, 187, 199, 0.22)",
                }}
              >
                {treatmentItems.map((item) => (
                  <ListItemButton
                    key={item.label}
                    onClick={() => goTo(item.href)}
                    sx={{
                      minHeight: 42,
                      mb: 0.4,
                      px: 1.4,
                      borderRadius: 2,
                      color: isActive(item.href) ? PRIMARY : BLACK,
                      backgroundColor: isActive(item.href)
                        ? "rgba(6, 187, 199, 0.08)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(6, 187, 199, 0.08)",
                        color: PRIMARY,
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: 650,
                        fontSize: "0.91rem",
                      }}
                    />
                  </ListItemButton>
                ))}
              </Box>
            </Collapse>

            <ListItemButton
              onClick={() => setStylingOpen((prev) => !prev)}
              sx={{
                minHeight: 48,
                mb: 0.6,
                px: 1.4,
                borderRadius: 2,
                color: isStylingActive() ? PRIMARY : BLACK,
                backgroundColor: isStylingActive()
                  ? "rgba(6, 187, 199, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(6, 187, 199, 0.08)",
                  color: PRIMARY,
                },
              }}
            >
              <ListItemText
                primary="Estilismo"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.96rem",
                }}
              />

              <KeyboardArrowDownIcon
                sx={{
                  transform: stylingOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  color: "inherit",
                }}
              />
            </ListItemButton>

            <Collapse in={stylingOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  ml: 1,
                  mb: 0.8,
                  pl: 1.2,
                  borderLeft: "2px solid rgba(6, 187, 199, 0.22)",
                }}
              >
                {stylingItems.map((item) => (
                  <ListItemButton
                    key={item.label}
                    onClick={() => goTo(item.href)}
                    sx={{
                      minHeight: 42,
                      mb: 0.4,
                      px: 1.4,
                      borderRadius: 2,
                      color: isActive(item.href) ? PRIMARY : BLACK,
                      backgroundColor: isActive(item.href)
                        ? "rgba(6, 187, 199, 0.08)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(6, 187, 199, 0.08)",
                        color: PRIMARY,
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: 650,
                        fontSize: "0.91rem",
                      }}
                    />
                  </ListItemButton>
                ))}
              </Box>
            </Collapse>

            <ListItemButton
              onClick={() => goTo("/tienda")}
              sx={{
                minHeight: 48,
                mb: 0.6,
                px: 1.4,
                borderRadius: 2,
                color: isActive("/tienda") ? PRIMARY : BLACK,
                backgroundColor: isActive("/tienda")
                  ? "rgba(6, 187, 199, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(6, 187, 199, 0.08)",
                  color: PRIMARY,
                },
              }}
            >
              <ListItemText
                primary="Tienda"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.96rem",
                }}
              />
            </ListItemButton>

            <ListItemButton
              onClick={() => goTo("/iniciar-sesion")}
              sx={{
                minHeight: 48,
                mb: 0.6,
                px: 1.4,
                borderRadius: 2,
                color: isActive("/iniciar-sesion") ? PRIMARY : BLACK,
                backgroundColor: isActive("/iniciar-sesion")
                  ? "rgba(6, 187, 199, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(6, 187, 199, 0.08)",
                  color: PRIMARY,
                },
              }}
            >
              <ListItemText
                primary="Iniciar sesión"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.96rem",
                }}
              />

              <LoginRoundedIcon sx={{ fontSize: 21, color: "inherit" }} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default NavbarMobile;